import React, { useEffect, useState, useMemo } from "react";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import { Button } from "../../components/Button";
import { notifyError } from "src/assets/toastService";
import InputBox from "../../components/InputBox";
import PopupModules from "src/features/auth/components/PopupModules";
import AddProduct from "./AddProduct";
import { CategoryV2, Product } from "src/shared/services/interfacev2";
import { Order } from "src/shared/types";
import { PaymentHistory as orderPym } from "src/shared/types";
import PaymentHistory from "./PaymentHistory";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";
import { splitDateTime } from "src/shared/components/dateTime";
import { useUser } from "src/shared/context/UserContext";

export const statusColors: Record<string, string> = {
  Confirm: "bg-blue-200 text-blue-800",
  confirm: "bg-blue-200 text-blue-800",
  Inprocess: "bg-yellow-200 text-yellow-800",
  "Waiting for Delivery": "bg-orange-200 text-orange-800",
  Delivered: "bg-green-200 text-green-800",
  Cancelled: "bg-red-200 text-red-800",
  Cancel: "bg-red-200 text-red-800",
  RETURN: "bg-purple-200 text-purple-800",
  REFUND: "bg-pink-200 text-pink-800",
  ADJUST: "bg-teal-200 text-teal-800",
  Adjusted: "bg-teal-200 text-teal-800",
  INITIAL: "bg-sky-200 text-sky-800",
  ORDER_PAGE: "bg-indigo-200 text-indigo-800",
  deleted: "bg-red-200 text-red-800",
};

const statusOption = ["Confirm", "Inprocess", "Waiting for Delivery", "Delivered"];

type Props = {
  order: Order;
  received: number;
  remainingPrice: string;
  statusChange: (value: string) => void;
  orderStatus: string;
  setPayment: (data: boolean) => void;
  setPayOnly: (data: boolean) => void;
  setPrintData: (data: string) => void;
  setHistory: (data: boolean) => void;
  setLastOrderData: (data: Order) => void;
  osb: number | string;
  editData: boolean;
  setEditData: (data: boolean) => void;
  setOrderChange: (data: Order) => void;
  product: Product[];
  category: CategoryV2[];
  productSync: () => void;
  loadingPro: boolean;
  sendToOrder: (productId: string) => void;
};

function OrderSidePanal({
  order,
  received,
  remainingPrice,
  statusChange,
  orderStatus,
  setPayOnly,
  setPayment,
  setPrintData,
  setHistory,
  setLastOrderData,
  osb,
  editData,
  setEditData,
  setOrderChange,
  category,
  product,
  loadingPro,
  productSync,
  sendToOrder,
}: Props) {
  const { userConfigration } = useUser();
  const [productPrice, setProductPrice] = useState({
    orderPrice: "0.00",
    discount: "0.00",
    finalAmount: "0.00",
    deliveryFee: "0.00",
    parcelFee: "0.00",
  });
  const [viewProduct, setViewProduct] = useState<boolean>(false);
  const [viewPaymentHistory, setViewPaymentHistory] = useState<boolean>(false);
  const [returnForm, setReturnForm] = useState({
    returnRefund: false,
    refundAmount: "0.00",
    adjustAmount: "0.00",
    reverseDiscount: "0.00",
    reverseParcelFee: "0.00",
    reverseDeliveryFee: "0.00",
  });
  const [orderResvedPrice, setOrderResvedPrice] = useState<number>(0);
  const { formatDate, formatTime } = useDateTimeFormatter();
  const now = new Date();

  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);

  // ── Get the latest values from orders ──────────────────────────────────────────
  const originalOrder = order?.orders?.find((i) => i.paymentSource === "ORDER_CREATED");
  const currentStatus = originalOrder?.status ?? "";

  // ✅ Get ALL RETURN orders using useMemo to prevent unnecessary re-renders
  const returnOrders = useMemo(
    () => order?.orders?.filter((i) => i.paymentSource === "RETURN") || [],
    [order?.orders]
  );

  // ✅ Get original values using useMemo
  const originalValues = useMemo(() => {
    const originalDiscount = Number(originalOrder?.discount || 0);
    const originalDeliveryFee = Number(originalOrder?.deliveryFee || 0);
    const originalParcelFee = Number(originalOrder?.parcelFee || 0);
    const orderFinalAmount = Number(originalOrder?.finalAmount || 0);
    const orderPrice = Number(originalOrder?.orderAmount ?? 0);

    return {
      originalDiscount,
      originalDeliveryFee,
      originalParcelFee,
      orderFinalAmount,
      orderPrice,
    };
  }, [originalOrder]);

  // ✅ Calculate reverse amounts from return orders using useMemo
  const reverseAmounts = useMemo(() => {
    let reverseDiscount = 0;
    let reverseParcelFee = 0;
    let reverseDeliveryFee = 0;
    let totalReturnAdjustment = 0;
    let totalReturnDiscount = 0;
    let totalReturnDeliveryFee = 0;
    let totalReturnParcelFee = 0;

    returnOrders.forEach((o) => {
      reverseDiscount += Number(o.discount || 0);
      reverseParcelFee += Number(o.parcelFee || 0);
      reverseDeliveryFee += Number(o.deliveryFee || 0);
      totalReturnAdjustment += Number((o as any).adjustAmount || 0);
      totalReturnDiscount += Number(o.discount || 0);
      totalReturnDeliveryFee += Number(o.deliveryFee || 0);
      totalReturnParcelFee += Number(o.parcelFee || 0);
    });

    // Calculate total return product amount from orderDetails
    const returnOrderDetails = order?.orderDetails?.filter(d => d.paymentSource === "RETURN") || [];
    let totalReturnProductAmount = 0;
    returnOrderDetails.forEach(detail => {
      detail.products?.forEach(p => {
        totalReturnProductAmount += Math.abs(Number(p.quantity || 0)) * Number(p.unitPrice || 0);
      });
    });

    return {
      reverseDiscount,
      reverseParcelFee,
      reverseDeliveryFee,
      totalReturnAdjustment,
      totalReturnDiscount,
      totalReturnDeliveryFee,
      totalReturnParcelFee,
      totalReturnProductAmount,
    };
  }, [returnOrders, order?.orderDetails]);

  const {
    reverseDiscount: revDiscount,
    reverseParcelFee: revParcelFee,
    reverseDeliveryFee: revDeliveryFee
  } = reverseAmounts;

  const {
    originalDiscount,
    originalDeliveryFee,
    originalParcelFee,
    orderFinalAmount,
    orderPrice,
  } = originalValues;

  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ");

  const { date: orderDate, time: orderTime } = splitDateTime(order?.orders?.[0]?.orderedOn.$date ?? "");

  // ── Handle return form state ────────────────────────────────────────────────────
  useEffect(() => {
    if (!order) return;

    const returnItems = order?.paymentHistory?.filter((detail) => detail.paymentSource === "RETURN") ?? [];

    if (returnItems.length !== 0) {
      const refundAmount = returnItems.reduce((acc, payment) => acc + Number(payment.receivedAmount ?? 0), 0);
      const osbAdjust = returnItems.reduce((acc, payment) => acc + Number(payment.adjustAmount ?? 0), 0);

      setReturnForm({
        returnRefund: true,
        refundAmount: Math.abs(refundAmount).toFixed(2),
        adjustAmount: Math.abs(osbAdjust).toFixed(2),
        reverseDiscount: revDiscount.toFixed(2),
        reverseParcelFee: revParcelFee.toFixed(2),
        reverseDeliveryFee: revDeliveryFee.toFixed(2),
      });
    }
  }, [order, revDiscount, revParcelFee, revDeliveryFee]);

  const refundTotal = Number(returnForm.refundAmount) + Number(returnForm.adjustAmount);
  const netTotal = orderFinalAmount - refundTotal;

  // ── Update product price display ──────────────────────────────────────────────
  useEffect(() => {
    // ✅ Show ORIGINAL values (before return)
    const discount = Number(productPrice.discount) || originalDiscount;
    const deliveryFee = Number(productPrice.deliveryFee) || originalDeliveryFee;
    const parcelFee = Number(productPrice.parcelFee) || originalParcelFee;

    // ✅ Calculate total price BEFORE return
    const finalAmount = orderPrice - discount + deliveryFee + parcelFee;

    setProductPrice({
      orderPrice: orderPrice.toFixed(2),
      discount: discount.toFixed(2),
      finalAmount: finalAmount.toFixed(2),
      deliveryFee: deliveryFee.toFixed(2),
      parcelFee: parcelFee.toFixed(2),
    });

    const resvedAmountData = order?.paymentHistory?.filter((payment) => payment.paymentSource !== "RETURN");
    const resvedPrice = resvedAmountData?.reduce((acc, payment) => acc + Number(payment.receivedAmount ?? 0), 0);
    setOrderResvedPrice(resvedPrice ?? 0);
  }, [order, orderPrice, originalDiscount, originalDeliveryFee, originalParcelFee, productPrice.discount, productPrice.deliveryFee, productPrice.parcelFee]);

  // ── Unique payment methods ────────────────────────────────────────────────────
  const uniquePaymentMethods =
    Array.from(new Set((order?.paymentHistory ?? []).filter((p) => p.paymentSource !== "RETURN" && p.paymentMethod).map((p) => p.paymentMethod as string))).join(", ") || "—";

  // ── Dropdown ──────────────────────────────────────────────────────────────────
  const renderDropdown = (value: string, title: string, name: string) => {
    const filteredOptions = statusOption.filter((s) => {
      if (currentStatus === "Confirm") return s !== "Waiting for Delivery" && s !== "Delivered";
      if (currentStatus === "Inprocess") return s !== "Confirm" && s !== "Delivered";
      if (currentStatus === "Waiting for Delivery") return s !== "Confirm" && s !== "Inprocess";
      return true;
    });

    if (!order) return <div>Loading...</div>;

    return (
      <div className="mb-3">
        <p id={`${name}-label`} className={`text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold ${ThemeBasedColorHeader()}`}>
          {title}
        </p>
        <select
          id={`${name}-select`}
          aria-labelledby={`${name}-label`}
          value={value}
          onChange={(e) => statusChange(e.target.value)}
          className={`w-full border border-gray-300 rounded px-2 py-2 text-lg min-h-[50px] ${ThemeBasedColorMenubar}`}
        >
          {filteredOptions.map((s) => (
            <option key={s} value={s} className="py-5">
              {s}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const handlePayBalance = () => {
    if (Number(osb) <= 0 && Number(remainingPrice) <= 0) {
      notifyError("Outstanding balance cleared via another order or ledger entry.");
      return;
    }
    setPayOnly(true);
    setPayment(true);
  };

  const rePrintData = () => {
    setHistory(false);
    setLastOrderData(order);
    setPrintData(now.toTimeString());
    setTimeout(() => {
      setPrintData("");
    }, 100);
  };

  const cancelEdit = () => {
    const discount = originalDiscount;
    const deliveryFee = originalDeliveryFee;
    const parcelFee = originalParcelFee;
    const finalAmount = orderPrice - discount + deliveryFee + parcelFee;

    setProductPrice({
      orderPrice: orderPrice.toFixed(2),
      discount: discount.toFixed(2),
      finalAmount: finalAmount.toFixed(2),
      deliveryFee: deliveryFee.toFixed(2),
      parcelFee: parcelFee.toFixed(2),
    });
    setEditData(false);
  };

  const isOsb = Number(osb) > 0;
  const totalSOB = Math.abs(Number(osb));
  const remainingAfter = orderFinalAmount < Number(orderResvedPrice) ? 0 : orderFinalAmount - Number(orderResvedPrice);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={`grid grid-cols-2 w-full lg:w-2/6 md:grid-cols-1 md:h-fit gap-2 h-full shadow-inner rounded-lg p-2 ${ThemeBasedColorMenubar()}`}>
      {/* ── Order Details ────────────────────────────────────────────────── */}
      <div className="w-full bg-white rounded-xl shadow-lg p-2 h-fit">
        <h1 className={`text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold ${ThemeBasedColorHeader()}`}>Order Details</h1>
        {[
          { label: "Order Id", value: order?.orderId },
          { label: "Name", value: customerName },
          {
            label: "Order Status",
            value: <span className={`px-2 py-1 rounded font-semibold text-sm ${statusColors[currentStatus] ?? "bg-gray-200 text-gray-700"}`}>{currentStatus}</span>,
          },
          { label: "Date", value: formatDate(orderDate) },
          { label: "Time", value: formatTime(orderTime) },
          { label: "Bill No", value: originalOrder?.billNumber },
        ].map(({ label, value }) => (
          <div key={label} className="flex justify-between items-center py-0.5">
            <p>{label}</p>
            <p>{value}</p>
          </div>
        ))}
      </div>

      {/* ── Payment Details ──────────────────────────────────────────────── */}
      <div className="w-full bg-white rounded-xl shadow-lg p-2 h-fit">
        <h1 className={`text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold ${ThemeBasedColorHeader()}`}>Payment Details</h1>

        {[
          { label: "Sub Total", value: productPrice.orderPrice },
          {
            label: "Discount (-)",
            value: productPrice.discount,
            editable: editData,
          },
          ...(deliveryFeeEnabled ? [{ label: "Delivery Fee (+)", value: productPrice.deliveryFee, editable: editData }] : []),
          ...(parcelFeeEnabled ? [{ label: "Parcel Fee (+)", value: productPrice.parcelFee, editable: editData }] : []),
          { label: "Total Price", value: productPrice.finalAmount },
          ...(returnForm.returnRefund ? [
            { label: "Return (-)", value: refundTotal.toFixed(2) },
          ] : []),
        ].map(({ label, value, editable }) => (
          <div key={label} className="grid grid-cols-2 items-center py-0.5">
            <p>{label}</p>
            {editable ? (
              <InputBox
                value={value}
                type="number"
                className="text-end"
                onChange={(e) => {
                  const newValue = Number(e.target.value) || 0;
                  const discount = Number(productPrice.discount) || 0;
                  const deliveryFee = Number(productPrice.deliveryFee) || 0;
                  const parcelFee = Number(productPrice.parcelFee) || 0;

                  if (label.includes("Discount")) {
                    setProductPrice((prev) => ({
                      ...prev,
                      discount: e.target.value,
                      finalAmount: (orderPrice - newValue + deliveryFee + parcelFee).toFixed(2),
                    }));
                  } else if (label.includes("Delivery Fee")) {
                    setProductPrice((prev) => ({
                      ...prev,
                      deliveryFee: e.target.value,
                      finalAmount: (orderPrice - discount + newValue + parcelFee).toFixed(2),
                    }));
                  } else if (label.includes("Parcel Fee")) {
                    setProductPrice((prev) => ({
                      ...prev,
                      parcelFee: e.target.value,
                      finalAmount: (orderPrice - discount + deliveryFee + newValue).toFixed(2),
                    }));
                  }
                }}
              />
            ) : (
              <p className="text-right">{value}</p>
            )}
          </div>
        ))}

        {/* ✅ Reverse Details section - show what was reversed */}
        {returnForm.returnRefund && (
          <>
            <div className="border-t border-dashed my-1"></div>
            {[
              { label: "Reverse Discount", value: returnForm.reverseDiscount },
              { label: "Reverse Parcel Fee", value: returnForm.reverseParcelFee },
              { label: "Reverse Delivery Fee", value: returnForm.reverseDeliveryFee },
            ].map(({ label, value }) => (
              <div key={label} className="grid grid-cols-2 items-center py-0.5">
                <p className="">{label}</p>
                <p className="text-right">{value}</p>
              </div>
            ))}
          </>
        )}

        {/* Return history section */}
        {returnForm.returnRefund && (
          <div className="flex flex-col border-y mt-1">
            {[
              { label: "Net Total", value: netTotal.toFixed(2) },
              { label: "Sum Received", value: orderResvedPrice.toFixed(2) },
              { label: "Refund (-)", value: returnForm.refundAmount },
            ].map(({ label, value }) => (
              <div key={label} className="grid grid-cols-2 items-center mt-1">
                <p>{label}</p>
                <p className="text-right">{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Summary rows */}
        {[
          {
            label: "Net Received",
            value: (Number(orderResvedPrice) - Number(returnForm.refundAmount)).toFixed(2),
          },
          {
            label: "Remaining",
            value: Number(Number(remainingAfter) - Number(returnForm.adjustAmount)).toFixed(2),
          },
          isOsb ? { label: "Total OSB", value: totalSOB.toFixed(2) } : { label: "Advance", value: totalSOB.toFixed(2) },
        ].map(({ label, value }) => (
          <div key={label} className="grid grid-cols-2 items-center mt-1">
            <p>{label}</p>
            <p className="text-right">{value}</p>
          </div>
        ))}

        {/* ── Payment Method row with History button ──────────────────── */}
        <div className="grid grid-cols-2 items-center mt-1">
          <p>Payment Method</p>
          <div className="flex items-center justify-end gap-1.5">
            <p className="text-right text-sm truncate max-w-[100px]" title={uniquePaymentMethods}>
              {uniquePaymentMethods}
            </p>
            {(order?.paymentHistory?.length ?? 0) > 1 && (
              <>
                <p onClick={() => setViewPaymentHistory(true)} className="text-blue-700 underline cursor-pointer">
                  (Payment History)
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Status Dropdown ──────────────────────────────────────────────── */}
      {currentStatus !== "Delivered" && currentStatus !== "Cancelled" && currentStatus !== "Returned" && (
        <div className="text-left mb-2 text-xl px-2 py-1 rounded-md font-semibold bg-white">{renderDropdown(orderStatus, "Update Order Status", "orderStatus")}</div>
      )}

      {/* ── Action Buttons ───────────────────────────────────────────────── */}
      {editData ? (
        <div className="w-full justify-around grid grid-cols-1 sm:grid-cols-3 gap-1">
          <Button label="Cancel" variant="cancel" onClick={cancelEdit} className="border shadow-md" />
          <Button
            label="Order Modify"
            variant="confirm"
            onClick={() =>
              setOrderChange({
                ...order,
                orders: [
                  {
                    ...(originalOrder as any),
                    discount: Number(productPrice.discount),
                    finalAmount: Number(productPrice.finalAmount),
                    orderAmount: Number(productPrice.orderPrice),
                    deliveryFee: Number(productPrice.deliveryFee),
                    parcelFee: Number(productPrice.parcelFee),
                  },
                ],
              })
            }
            className="border shadow-md"
          />
          <Button label="Add Product" variant="ThemeBase" onClick={() => setViewProduct(true)} className="border shadow-md" />
        </div>
      ) : (
        <div className="w-full justify-around grid grid-cols-1 sm:grid-cols-2 gap-1">
          <Button label="Reprint" variant="secondary" onClick={rePrintData} className="border shadow-md" />
          {currentStatus !== "Cancelled" && <Button label="Pay Balance" variant="confirm"  disabled={Number(remainingPrice) === 0 || currentStatus === "Delivered"} onClick={handlePayBalance} />}
        </div>
      )}

      {/* ── Add Product Popup ─────────────────────────────────────────────── */}
      <PopupModules isOpen={viewProduct} onClose={() => setViewProduct(false)} title="Add Product" className="w-1/2 h-5/6">
        <AddProduct sendToOrder={sendToOrder} product={product} category={category} syncProduct={productSync} loadingPro={loadingPro} />
      </PopupModules>

      {/* ── Payment History Popup ─────────────────────────────────────────── */}
      <PaymentHistory isOpen={viewPaymentHistory} onClose={() => setViewPaymentHistory(false)} paymentHistory={order?.paymentHistory ?? []} />
    </div>
  );
}

export default OrderSidePanal;